import { Box, Dialog, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import React from 'react';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PaylinkFilters } from '../PaylinkFiltersPanel/PaylinkFillters';
import {
  PaylinkFiltersFormValues,
  paylinkFiltersFormValuesToDownloadBody,
  usePaylinkFiltersForm,
} from '../../../../hooks/usePaylinkFiltersForm';
import { Button } from '../../../../components/core';
import { ButtonType } from '../../../../components/core/Button/Button';
import { useDownloadPaylinks } from '../../hooks';
import { DialogCloseButton } from '../../../../components/core/DialogCloseButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
}

const maxDateRange: [number, dayjs.ManipulateType] = [30, 'day'];

export const DownloadPaylinksDialog = ({ isOpen, onClose: onCloseProp, merchantId }: Props) => {
  const { paylinkPaymentMethodFilter } = useFlags();

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
    handleSubmit,
    trigger,
  } = usePaylinkFiltersForm({
    maxDateRange,
    dateRangeRequirement: 'createdOrTransactionExclusive',
  });
  const { onDownloadPaylinks, isLoading } = useDownloadPaylinks({ merchantId });

  const onClose = () => {
    reset();
    onCloseProp();
  };

  const onSubmit = async (values: PaylinkFiltersFormValues) => {
    const isValid = await trigger();
    if (!isValid) return Promise.resolve();

    const body = paylinkFiltersFormValuesToDownloadBody(values, paylinkPaymentMethodFilter);

    return onDownloadPaylinks(body).then(onClose);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          overflow: 'hidden',
          borderRadius: 4,
          '& h2, h3, p': {},
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '20px', fontWeight: 600 }}>Export Paylinks</DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent sx={{ overflowX: 'hidden' }} dividers>
        <Typography sx={{ fontSize: '14px' }}>
          Use the filters below to specify the Paylinks you would like to be included in your CSV
          file. Provide either a Creation Date Range or a Transaction Date Range (not both). The
          file will include transaction information associated with the Paylinks. An email will be
          sent to you when the CSV is ready to download.
        </Typography>
        <Typography component="h3" fontWeight="semibold" variant="large" sx={{ mt: 2, mb: 1 }}>
          Filters
        </Typography>

        <Divider />
        <Box
          component="form"
          id="download-paylinks"
          onSubmit={handleSubmit((values) => onSubmit(values))}
        >
          <Box sx={{ '& > *': { mt: (t) => `${t.spacing(2)} !important` } }}>
            <PaylinkFilters
              merchantId={merchantId}
              maxDateRange={maxDateRange}
              showTransactionDateRange
              hookFormProps={{
                watch,
                setValue,
                control,
                errors,
              }}
            />
          </Box>
        </Box>
        <Divider />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button text="Cancel" theme="secondary" onClick={onClose} isFullWidth />
          <Button
            text="Export Paylinks"
            theme="primary"
            buttonType={ButtonType.SUBMIT}
            form="download-paylinks"
            isFullWidth
            loading={isLoading}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
